import { Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { FaTrash, FaUserTag } from "react-icons/fa";
import toast from "react-hot-toast";
import { Modal, ModalBody, Button } from "reactstrap";
import GeneralButton from "../button/GeneralButton";
import { getAllCustomers, updateUserWhitelistAPI, getUserWhitelistAPI, verifyWhitelistPropagationAPI } from "../../api/pagination";


const AssignCustomersModal = ({ modal, toggle, user }) => {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchCustomers, setSearchCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedForRemoval, setSelectedForRemoval] = useState([]);
  const [whitelistedCustomers, setWhitelistedCustomers] = useState([]);
  const [fetchingWhitelisted, setFetchingWhitelisted] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (modal && user) {
      fetchWhitelistedCustomers();
      handleCustomerSearch("");
      setVerificationResult(null);
    } else if (!modal) {
      setVerificationResult(null);
    }
    return () => {
      isMounted.current = false;
    };
  }, [modal, user]);

  const fetchWhitelistedCustomers = () => {
    setFetchingWhitelisted(true);
    getUserWhitelistAPI(user._id, (res) => {
      if (!isMounted.current) return;
      if (res.status === 200) {
        const customersData = res.data?.data || res.data?.customers || (Array.isArray(res.data) ? res.data : []);
        setWhitelistedCustomers(customersData);
      } else {
        console.error("Failed to fetch whitelisted customers:", res.message);
        setWhitelistedCustomers([]);
      }
      setFetchingWhitelisted(false);
    });
  };

  const debounceTimeout = React.useRef(null);

  const handleCustomerSearch = async (searchText) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // For immediate empty string search on mount or clear
    if (!searchText) {
        setDataLoading(true);
        getAllCustomers(1, searchText, 50, "", (res) => {
          if (!isMounted.current) return;
          if (res && res.status === 200) {
            setSearchCustomers(res?.data?.data);
            setCustomers(res?.data?.data);
          }
          setDataLoading(false);
        });
        return;
    }

    // Debounced search for typing
    debounceTimeout.current = setTimeout(() => {
      setDataLoading(true);
      getAllCustomers(1, searchText, 50, "", (res) => {
        if (!isMounted.current) return;
        if (res && res.status === 200) {
          setSearchCustomers(res?.data?.data);
          setCustomers(res?.data?.data);
        }
        setDataLoading(false);
      });
    }, 500);
  };

  const handleSelectAllToggle = (checked) => {
    setSelectAll(checked);
  };

  const handleAssignCustomer = () => {
    if (!selectAll && (!selectedCustomers || selectedCustomers.length === 0)) {
      toast.error("Please select at least one customer.");
      return;
    }

    if (!selectAll) {
      const newCustomers = selectedCustomers.filter(id => !whitelistedCustomers.some(c => c._id === id));
      if (newCustomers.length === 0) {
        toast.error("All selected customers are already assigned.");
        return;
      }

      setLoading(true);
      updateUserWhitelistAPI(user._id, newCustomers, "add", false, (res) => {
        if (!isMounted.current) return;
        if (res.status === 200) {
          toast.success(res.data?.message || "Customer(s) assigned successfully.");
          const addedCustomers = (searchCustomers.length > 0 ? searchCustomers : customers).filter(c => newCustomers.includes(c._id));
          if (addedCustomers.length > 0) {
            setWhitelistedCustomers(prev => {
              const unique = addedCustomers.filter(addC => !prev.some(p => p._id === addC._id));
              return [...prev, ...unique];
            });
          } else {
            fetchWhitelistedCustomers();
          }
          setSelectedCustomers([]);
          setSelectAll(false);
        } else {
          toast.error(res?.message || res?.data?.message || "Failed to assign customer.");
        }
        setLoading(false);
      });
    } else {
      // selectAll = true: send selectAll flag to backend
      setLoading(true);
      updateUserWhitelistAPI(user._id, [], "add", true, (res) => {
        if (!isMounted.current) return;
        if (res.status === 200) {
          toast.success(res.data?.message || "All customers assigned successfully.");
          fetchWhitelistedCustomers();
          setSelectedCustomers([]);
          setSelectAll(false);
        } else {
          toast.error(res?.message || res?.data?.message || "Failed to assign all customers.");
        }
        setLoading(false);
      });
    }
  };

  const handleRemoveCustomer = (customerId) => {
    setLoading(true);
    updateUserWhitelistAPI(user._id, [customerId], "remove", false, (res) => {
      if (!isMounted.current) return;
      if (res.status === 200) {
        toast.success("Customer removed successfully.");
        fetchWhitelistedCustomers();
        setSelectedForRemoval(prev => prev.filter(id => id !== customerId));
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to remove customer.");
      }
      setLoading(false);
    });
  };

  const handleRemoveSelected = () => {
    if (selectedForRemoval.length === 0) return;
    setLoading(true);
    updateUserWhitelistAPI(user._id, selectedForRemoval, "remove", false, (res) => {
      if (!isMounted.current) return;
      if (res.status === 200) {
        toast.success("Customers removed successfully.");
        fetchWhitelistedCustomers();
        setSelectedForRemoval([]);
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to remove customers.");
      }
      setLoading(false);
    });
  };
  const filteredWhitelistedCustomers = whitelistedCustomers.filter(customer => {
    if (!localSearchQuery) return true;
    const q = localSearchQuery.toLowerCase();
    const name = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    const company = (customer.company || customer?.billing?.[0]?.company || '').toLowerCase();
    const email = (customer.email || '').toLowerCase();
    return name.includes(q) || company.includes(q) || email.includes(q);
  });

  const handleVerifyPropagation = (fix = false) => {
    setIsVerifying(true);
    verifyWhitelistPropagationAPI(user._id, fix, (res) => {
      if (!isMounted.current) return;
      if (res.status === 200) {
        setVerificationResult(res.data);
        if (fix) {
          toast.success("Propagation fixed successfully.");
        } else {
          toast.success("Verification audit complete.");
        }
      } else {
        toast.error(res?.message || "Failed to verify propagation.");
      }
      setIsVerifying(false);
    });
  };


  return (
    <Modal isOpen={modal} toggle={toggle} centered size="xl">
      <ModalBody className="p-0 bg-white rounded-4 overflow-hidden shadow-lg">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-bottom">
          <div className="d-flex align-items-center gap-2">
            <FaUserTag size={22} className="text-primary" />
            <h5 className="fw-bold m-0 text-dark">Assign Customers to {user?.username}</h5>
          </div>
          <IoCloseSharp
            size={24}
            className="pointer text-muted hover-opacity-75"
            onClick={toggle}
          />
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="fw-bold m-0">Select Customer(s)</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="selectAllCustomers"
                  className="form-check-input pointer m-0"
                  checked={selectAll}
                  onChange={(e) => handleSelectAllToggle(e.target.checked)}
                  disabled={dataLoading}
                />
                <label htmlFor="selectAllCustomers" className="form-check-label pointer mb-0 small text-muted fw-semibold">
                  Select All
                </label>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Select
                mode="multiple"
                size="large"
                showSearch
                loading={dataLoading}
                notFoundContent={dataLoading ? <Spin size="small" /> : "No customers found"}
                placeholder="Search Customer by Name/Company"
                filterOption={false}
                onChange={(val) => {
                  setSelectedCustomers(val);
                  setSelectAll(false);
                }}
                onSearch={(input) => handleCustomerSearch(input)}
                value={selectedCustomers}
                className="flex-grow-1"
                getPopupContainer={(trigger) => trigger.parentNode}
                options={(searchCustomers?.length > 0 ? searchCustomers : customers)?.map((option) => ({
                  value: option._id,
                  label: `${option?.id || ''} - ${option?.billing?.[0]?.company || option?.company || 'Unknown'}`,
                }))}
              />
              <GeneralButton
                title={selectAll ? "Add All" : "Add"}
                className="blue-button"
                width="100px"
                handleClick={handleAssignCustomer}
                loading={loading}
                disabled={loading || (!selectAll && selectedCustomers.length === 0)}
              />
            </div>
            {selectAll && (
              <p className="text-primary small mt-1 mb-0">
                <strong>Select All</strong> is active — all customers will be assigned to this user.
              </p>
            )}
          </div>

          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold m-0 text-secondary text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '0.85rem' }}>
                Assigned Customers
              </h6>
              <div className="d-flex align-items-center gap-3">
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-pill px-3 shadow-sm border"
                    style={{ width: '250px', fontSize: '0.85rem', paddingRight: '30px' }}
                    placeholder="Search assigned customers..."
                    value={localSearchQuery}
                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                  />
                  {localSearchQuery && (
                    <IoCloseSharp
                      size={16}
                      className="text-muted pointer position-absolute"
                      style={{ top: '50%', right: '10px', transform: 'translateY(-50%)', cursor: 'pointer' }}
                      onClick={() => setLocalSearchQuery("")}
                    />
                  )}
                </div>
                {selectedForRemoval.length > 0 && (
                  <Button
                    color="danger"
                    className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill shadow-sm border-0"
                    style={{ fontSize: '0.8rem' }}
                    onClick={handleRemoveSelected}
                    disabled={loading}
                  >
                    <FaTrash size={12} /> Remove Selected ({selectedForRemoval.length})
                  </Button>
                )}
              </div>
            </div>
            {fetchingWhitelisted ? (
              <div className="text-center py-5">
                <Spin size="large" />
              </div>
            ) : whitelistedCustomers.length > 0 ? (
              <div className="table-responsive border rounded-3 shadow-sm" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-sm table-bordered table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="bg-light border-bottom" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th className="py-2 px-3 text-center align-middle" style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input pointer m-0"
                            checked={filteredWhitelistedCustomers.length > 0 && filteredWhitelistedCustomers.every(c => selectedForRemoval.includes(c.docId || c._id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newIds = filteredWhitelistedCustomers.map(c => c.docId || c._id);
                                setSelectedForRemoval(prev => Array.from(new Set([...prev, ...newIds])));
                              } else {
                                const removeIds = filteredWhitelistedCustomers.map(c => c.docId || c._id);
                                setSelectedForRemoval(prev => prev.filter(id => !removeIds.includes(id)));
                              }
                            }}
                          />
                      </th>
                      <th className="py-2 px-3 text-muted fw-semibold text-uppercase align-middle" style={{ fontSize: '0.75rem' }}>Customer Name</th>
                      <th className="py-2 px-3 text-muted fw-semibold text-uppercase align-middle" style={{ fontSize: '0.75rem' }}>Company</th>
                      <th className="py-2 px-3 text-muted fw-semibold text-uppercase align-middle" style={{ fontSize: '0.75rem' }}>Email</th>
                      <th className="py-2 px-3 text-center text-muted fw-semibold text-uppercase align-middle" style={{ fontSize: '0.75rem', width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWhitelistedCustomers.length > 0 ? (
                      filteredWhitelistedCustomers.map((customer) => {
                      const docId = customer.docId || customer._id;
                      const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
                      const company = customer.company || customer?.billing?.[0]?.company || 'N/A';
                      const email = customer.email || 'N/A';

                      return (
                        <tr key={docId || Math.random()} className="align-middle border-bottom">
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              className="form-check-input pointer m-0"
                              checked={selectedForRemoval.includes(docId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedForRemoval(prev => [...prev, docId]);
                                } else {
                                  setSelectedForRemoval(prev => prev.filter(id => id !== docId));
                                }
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 fw-bold text-dark">{name}</td>
                          <td className="px-3 py-2 text-secondary">{company}</td>
                          <td className="px-3 py-2 text-muted">{email}</td>
                          <td className="px-3 py-2 text-center">
                            <Button
                              color="link"
                              className="p-1 border-0 text-danger hover-opacity-75 bg-danger-soft rounded-circle"
                              style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              onClick={() => handleRemoveCustomer(docId)}
                              disabled={loading}
                              title="Remove Customer"
                            >
                              <FaTrash size={12} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No matching assigned customers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5 text-muted bg-light rounded-3 border border-dashed">
                <div className="mb-2">
                  <FaUserTag size={36} className="text-secondary opacity-50" />
                </div>
                <h6 className="fw-bold mb-1">No Customers Assigned</h6>
                <p className="small mb-0">Search and select customers above to assign them.</p>
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold m-0 text-secondary text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '0.85rem' }}>
                System Tools
              </h6>
            </div>
            <div className="bg-light p-3 rounded-3 border">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="fw-bold mb-1">Verify Whitelist Propagation</h6>
                  <p className="small text-muted mb-0">Check if this user's assigned customers are correctly synced with their orders and quotes.</p>
                </div>
                <div className="d-flex gap-2">
                  <GeneralButton 
                    title="Audit Data" 
                    className="blue-button" 
                    handleClick={() => handleVerifyPropagation(false)} 
                    loading={isVerifying} 
                  />
                  {verificationResult && (verificationResult?.orders?.missingCount > 0 || verificationResult?.quotes?.missingCount > 0) && (
                    <Button 
                      color="danger" 
                      className="rounded-2 fw-semibold"
                      onClick={() => handleVerifyPropagation(true)}
                      disabled={isVerifying}
                    >
                      Apply Fix
                    </Button>
                  )}
                </div>
              </div>
              
              {verificationResult && (
                <div className="bg-white p-3 rounded border shadow-sm mt-3" style={{ fontSize: '0.85rem' }}>
                  <div className="mb-2 d-flex gap-3">
                    <span className="badge bg-primary">Mode: {verificationResult.mode}</span>
                    <span className="badge bg-secondary">Total Customers: {verificationResult.whitelistedCustomersCount}</span>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-2">
                        <h6 className="fw-bold border-bottom pb-1 mb-2">Orders</h6>
                        <div className="d-flex justify-content-between mb-1"><span>Total Found:</span> <strong>{verificationResult.orders?.totalFound}</strong></div>
                        <div className="d-flex justify-content-between mb-1 text-danger"><span>Missing Access:</span> <strong>{verificationResult.orders?.missingCount}</strong></div>
                        {verificationResult.mode === 'fix' && (
                          <div className="d-flex justify-content-between text-success"><span>Repaired:</span> <strong>{verificationResult.orders?.repairedCount}</strong></div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-2">
                        <h6 className="fw-bold border-bottom pb-1 mb-2">Quotes</h6>
                        <div className="d-flex justify-content-between mb-1"><span>Total Found:</span> <strong>{verificationResult.quotes?.totalFound}</strong></div>
                        <div className="d-flex justify-content-between mb-1 text-danger"><span>Missing Access:</span> <strong>{verificationResult.quotes?.missingCount}</strong></div>
                        {verificationResult.mode === 'fix' && (
                          <div className="d-flex justify-content-between text-success"><span>Repaired:</span> <strong>{verificationResult.quotes?.repairedCount}</strong></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>


        </div>
      </ModalBody>
    </Modal>
  );
};

export default AssignCustomersModal;
